/**
 * VidROI Marketing Homepage — what visitors see at vidroi.io/ when signed out.
 *
 * Structure (top → bottom):
 *   1. Sticky nav with sign-in
 *   2. Hero: headline + sub + dual CTA + animated dashboard mockup
 *   3. Pain → solution callout strip
 *   4. Feature triplet: Attribution / Auto-injection / Ask AI
 *   5. Competitor analysis section (the differentiator)
 *   6. Demo video CTA (placeholder modal — swap iframe URL when ready)
 *   7. Pricing section — Starter $29 / Pro $79 side-by-side (Feb 2026 two-tier)
 *   8. FAQ
 *   9. Footer
 *
 * Everything is static JSX with the existing brand aesthetic (amber→red gradient,
 * dark zinc background, mono labels, sharp/rounded card mix). Mockup dashboards
 * are hand-built HTML — not real screenshots — so the homepage stays in sync
 * with the brand even if the actual product UI evolves.
 */
import React, { useState, useEffect } from "react";
import {
  Play, TrendingUp, Target, Sparkles, Search, ArrowRight, Check, X,
  Zap, ShieldCheck, BarChart3, Youtube, MousePointerClick, DollarSign,
  Eye, MessageSquare, ThumbsUp, ChevronDown,
} from "lucide-react";
import { SiteFooter } from "./SiteFooter";

/** Tiny in-house collapsible used by the FAQ — avoids pulling shadcn Accordion. */
function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5 py-5">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left group"
        data-testid={`faq-toggle-${q.slice(0, 20).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
      >
        <span className="text-white font-medium group-hover:text-amber-200 transition-colors">{q}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="text-zinc-400 leading-relaxed mt-3 pr-8">{a}</p>}
    </div>
  );
}

/** Video modal — placeholder until we have the real demo URL. */
function VideoModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-modal"
    >
      <div
        className="relative w-full max-w-3xl bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-black/90 text-white flex items-center justify-center"
          data-testid="video-modal-close"
        >
          <X className="w-4 h-4" />
        </button>
        {/* Placeholder — swap with real iframe when demo video is ready:
            <iframe src="https://www.youtube.com/embed/YOUR_VIDEO_ID" ... /> */}
        <div className="aspect-video bg-gradient-to-br from-zinc-900 to-black flex flex-col items-center justify-center text-center px-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center mb-5 shadow-[0_0_40px_-5px_rgba(255,80,0,0.6)]">
            <Play className="w-9 h-9 text-white ml-1" />
          </div>
          <h3 className="text-2xl font-semibold text-white mb-2">Demo video coming soon</h3>
          <p className="text-zinc-400 max-w-md leading-relaxed">
            We're recording a 2-minute walkthrough showing how VidROI matches
            YouTube views to actual sales. In the meantime, start the free
            trial — you'll see your own data in 90 seconds.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Mock dashboard card used in the hero — stylised, never breaks. */
function DashboardMockup() {
  const rows = [
    { thumb: "1", title: "How I made $50k from one YouTube video", views: "42.1k", ctr: "4.3%", sales: 34, rev: "$5,100", color: "emerald" },
    { thumb: "2", title: "My morning routine for productivity", views: "18.9k", ctr: "1.8%", sales: 8, rev: "$480", color: "zinc" },
    { thumb: "3", title: "Why most creators fail (controversial)", views: "28.4k", ctr: "0.8%", sales: 0, rev: "$0", color: "red" },
    { thumb: "4", title: "Tour of my home studio setup", views: "5.2k", ctr: "1.8%", sales: 2, rev: "$120", color: "zinc" },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-zinc-950/95 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden">
      {/* Faux browser bar */}
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-black/60">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500/60"></span>
        <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60"></span>
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60"></span>
        <span className="ml-4 text-[11px] font-mono text-zinc-500">vidroi.io/dashboard</span>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-4 gap-px bg-white/[0.03]">
        {[
          { label: "Total revenue", value: "$45,230", delta: "+12.4%", icon: DollarSign, accent: "emerald" },
          { label: "Total views", value: "1.42M", delta: "+8.1%", icon: Eye, accent: "amber" },
          { label: "Avg CTR", value: "3.8%", delta: "+0.6%", icon: MousePointerClick, accent: "amber" },
          { label: "Avg EPC", value: "$1.97", delta: "+$0.34", icon: TrendingUp, accent: "emerald" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-zinc-950 p-3.5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{s.label}</span>
                <Icon className={`w-3.5 h-3.5 ${s.accent === "emerald" ? "text-emerald-400" : "text-amber-300"}`} />
              </div>
              <div className="text-xl font-bold text-white">{s.value}</div>
              <div className={`text-[11px] font-mono mt-0.5 ${s.accent === "emerald" ? "text-emerald-400" : "text-amber-300"}`}>{s.delta}</div>
            </div>
          );
        })}
      </div>

      {/* Video rows */}
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-zinc-300 font-medium">Top videos by revenue</span>
          <span className="text-[11px] text-zinc-500 font-mono">Last 30 days</span>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
            <div className={`w-14 h-9 rounded flex items-center justify-center text-[10px] font-mono font-bold ${
              r.color === "emerald" ? "bg-emerald-500/15 text-emerald-300" :
              r.color === "red" ? "bg-red-500/10 text-red-300" :
              "bg-white/[0.05] text-zinc-500"
            }`}>
              {r.thumb}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white truncate font-medium">{r.title}</div>
              <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                {r.views} views · CTR {r.ctr} · {r.sales} sales
              </div>
            </div>
            <div className={`text-sm font-bold font-mono ${
              r.color === "emerald" ? "text-emerald-300" :
              r.color === "red" ? "text-red-300" :
              "text-zinc-300"
            }`}>
              {r.rev}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Mock competitor-analysis card — second screenshot block. */
function CompetitorMockup() {
  return (
    <div className="rounded-2xl border border-white/10 bg-zinc-950/95 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden">
      <div className="px-5 py-3 border-b border-white/5 bg-black/60 flex items-center gap-2">
        <Search className="w-4 h-4 text-amber-300" />
        <span className="text-sm text-white font-semibold">Competitor Analysis</span>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 font-mono">@MrBeast · top 5</span>
      </div>
      <div className="p-5 space-y-3">
        {[
          { title: "I Survived 50 Hours In Antarctica", views: "284M", likes: "9.1M", comments: "412k", hook: "I'm currently 1,200 miles from civilization in the coldest place on Earth, and the camera battery just died — so let me explain what happens next while I still can…" },
          { title: "$1,000,000 Hide and Seek", views: "176M", likes: "5.4M", comments: "287k", hook: "Ten people are about to compete in the largest game of hide-and-seek ever filmed, and the winner walks away with a literal million dollars in cash — but there's a catch the camera doesn't show…" },
        ].map((v, i) => (
          <div key={i} className="rounded-lg border border-white/5 bg-white/[0.02] p-3.5">
            <div className="flex items-start gap-3">
              <div className="w-16 h-10 rounded bg-gradient-to-br from-amber-500/30 to-red-500/30 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                <Play className="w-4 h-4 text-amber-200" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium leading-tight mb-1">{v.title}</div>
                <div className="flex gap-3 text-[11px] text-zinc-500 font-mono">
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{v.views}</span>
                  <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{v.likes}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{v.comments}</span>
                </div>
              </div>
            </div>
            <div className="mt-2.5 pl-19">
              <span className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">First 300 words of the hook</span>
              <p className="text-xs text-zinc-300 italic leading-relaxed mt-1">"{v.hook}"</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Real product screenshots — not mockups. Lives below the feature triplet
 * to give credibility before the conversion section. Horizontal scroll on
 * mobile, click-to-zoom on desktop. */
function ScreenshotsCarousel() {
  const [zoom, setZoom] = useState(null);
  const shots = [
    {
      src: "/screenshots/ask-ai.jpeg",
      title: "Ask AI",
      caption: "Claude reads your data and answers questions like a real analyst.",
    },
    {
      src: "/screenshots/competitors.jpeg",
      title: "Competitor hook teardowns",
      caption: "Type any channel → top 5 videos with views, likes, hook transcripts.",
    },
    {
      src: "/screenshots/integrations.jpeg",
      title: "Integrations",
      caption: "Stripe direct, Shopify, generic webhook — pick whatever your stack uses.",
    },
  ];

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  return (
    <section className="py-20 border-t border-white/5 bg-gradient-to-b from-transparent to-black/40" data-testid="screenshots-carousel">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold mb-3">Inside the app</div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-3">Real screenshots, not stock art.</h2>
          <p className="text-zinc-400">Tap any image to zoom in. This is exactly what you see after sign-up.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {shots.map((s) => (
            <button
              type="button"
              key={s.src}
              onClick={() => setZoom(s)}
              className="group text-left rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-amber-500/30 transition-colors"
              data-testid={`screenshot-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={s.src}
                  alt={`${s.title} — VidROI screenshot`}
                  loading="lazy"
                  className="w-full aspect-[16/10] object-cover object-left-top group-hover:scale-[1.02] transition-transform"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-3">
                  <span className="text-xs text-white/80 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-md border border-white/10">
                    Click to zoom
                  </span>
                </div>
              </div>
              <div className="p-4">
                <div className="text-white font-semibold mb-1">{s.title}</div>
                <div className="text-sm text-zinc-400 leading-relaxed">{s.caption}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {zoom && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setZoom(null)}
          data-testid="screenshot-lightbox"
        >
          <button
            type="button"
            onClick={() => setZoom(null)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white flex items-center justify-center border border-white/10"
            data-testid="screenshot-lightbox-close"
          >
            <X className="w-4 h-4" />
          </button>
          <img
            src={zoom.src}
            alt={zoom.title}
            className="max-w-full max-h-[90vh] rounded-xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}

export default function MarketingHomepage() {
  const [videoOpen, setVideoOpen] = useState(false);

  const goToLogin = () => { window.location.href = "/login"; };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 font-sans selection:bg-amber-500/30">
      {/* ===== Nav ===== */}
      <nav className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <a href="/" className="text-xl font-bold text-white" data-testid="nav-logo">
            Vid<span className="text-red-500">ROI</span>
          </a>
          <div className="flex items-center gap-3">
            <a href="#pricing" className="text-sm text-zinc-400 hover:text-white transition-colors hidden sm:inline" data-testid="nav-pricing">Pricing</a>
            <a href="#features" className="text-sm text-zinc-400 hover:text-white transition-colors hidden sm:inline" data-testid="nav-features">Features</a>
            <button
              type="button"
              onClick={goToLogin}
              className="text-sm text-zinc-300 hover:text-white px-3 py-1.5 rounded-md transition-colors"
              data-testid="nav-signin"
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={goToLogin}
              className="text-sm bg-gradient-to-br from-amber-500 to-red-500 hover:from-amber-400 hover:to-red-400 text-white font-semibold px-4 py-2 rounded-md shadow-[0_4px_16px_-4px_rgba(255,80,0,0.5)] transition-all"
              data-testid="nav-cta"
            >
              Start free trial
            </button>
          </div>
        </div>
      </nav>

      {/* ===== Hero ===== */}
      <section className="relative pt-20 pb-24 overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-b from-red-500/10 via-amber-500/5 to-transparent blur-3xl pointer-events-none"></div>

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-xs text-zinc-400 mb-6" data-testid="hero-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Built for YouTube creators selling courses, software, or merch
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] mb-5" data-testid="hero-headline">
                See which YouTube video <span className="bg-gradient-to-br from-amber-300 to-red-500 bg-clip-text text-transparent">made the sale.</span>
              </h1>
              <p className="text-lg text-zinc-400 leading-relaxed mb-8 max-w-xl" data-testid="hero-subhead">
                Stop guessing. VidROI matches every customer back to the exact
                video that drove them — so you finally know what to make next.
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                <button
                  type="button"
                  onClick={goToLogin}
                  className="group bg-gradient-to-br from-amber-500 to-red-500 hover:from-amber-400 hover:to-red-400 text-white font-semibold px-6 py-3.5 rounded-lg shadow-[0_8px_32px_-8px_rgba(255,80,0,0.6)] hover:shadow-[0_12px_40px_-8px_rgba(255,80,0,0.85)] transition-all flex items-center gap-2"
                  data-testid="hero-cta-primary"
                >
                  Start free 14-day trial
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  className="flex items-center gap-2 text-zinc-300 hover:text-white px-6 py-3.5 rounded-lg border border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.04] transition-all font-semibold"
                  data-testid="hero-cta-video"
                >
                  <Play className="w-4 h-4" /> Watch 2-min demo
                </button>
              </div>
              <p className="text-xs text-zinc-500 flex items-center gap-1.5" data-testid="hero-trust">
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                Cancel anytime · no setup fees · card not charged until day 15
              </p>
            </div>

            {/* Mockup */}
            <div className="relative" data-testid="hero-mockup">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Pain → Solution strip ===== */}
      <section className="border-y border-white/5 bg-black/40 py-12">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-8">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-red-400 font-semibold mb-3">Without VidROI</div>
            <h3 className="text-2xl font-semibold text-white mb-4">"I have 200k subs but I have no idea which video pays my bills."</h3>
            <ul className="space-y-2.5 text-zinc-400">
              {["YouTube Analytics tells you views — not buyers.", "UTM links break the moment a viewer copies-pastes.", "Sales data lives in Stripe, traffic in YouTube, never connected.", "You make a follow-up video to the wrong winner."].map((p) => (
                <li key={p} className="flex gap-3"><X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" /><span>{p}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-400 font-semibold mb-3">With VidROI</div>
            <h3 className="text-2xl font-semibold text-white mb-4">Every sale tagged to the video that drove it. Within 90 seconds of signup.</h3>
            <ul className="space-y-2.5 text-zinc-200">
              {["Per-video revenue, CTR, EPC, conversion rate.", "Bulk-inject tracking links into every video on your channel — one click, signed and tamper-proof.", "Stripe + Shopify sales sync back automatically.", "Ask AI: \"Which thumbnail style converts?\" — get answers from YOUR data."].map((p) => (
                <li key={p} className="flex gap-3"><Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /><span>{p}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== Feature triplet ===== */}
      <section id="features" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold mb-3">What you get</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-4">Three tools that pay for themselves on the first sale.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Target,
                title: "Sales attribution",
                copy: "HMAC-signed tracking pixel + UTM links auto-injected into every video description in bulk. Tag your whole back-catalogue in one click — no manual copy-paste, no broken links.",
                accent: "amber",
              },
              {
                icon: Sparkles,
                title: "Ask AI",
                copy: "Built-in Claude analyst that reads YOUR data. \"What's my best offer this month?\" — instant answer with real numbers, not generic advice.",
                accent: "red",
              },
              {
                icon: Search,
                title: "Competitor hook teardowns",
                copy: "Paste any channel name → get the top 5 videos with view counts, likes, and the first 300 words of each hook transcribed by Whisper, plus an AI breakdown of the copywriting framework used.",
                accent: "emerald",
              },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-6 hover:border-white/20 transition-colors">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${
                    f.accent === "amber" ? "bg-amber-500/10 border border-amber-500/30" :
                    f.accent === "red" ? "bg-red-500/10 border border-red-500/30" :
                    "bg-emerald-500/10 border border-emerald-500/30"
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      f.accent === "amber" ? "text-amber-300" :
                      f.accent === "red" ? "text-red-300" :
                      "text-emerald-300"
                    }`} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{f.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{f.copy}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== Real-screenshots carousel — proof, not mockups ===== */}
      <ScreenshotsCarousel />

      {/* ===== Competitor analysis showcase ===== */}
      <section className="py-24 border-t border-white/5 bg-gradient-to-b from-black/40 to-transparent">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="lg:order-2">
              <CompetitorMockup />
            </div>
            <div className="lg:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/[0.08] border border-amber-500/20 text-xs text-amber-300 mb-5">
                <Search className="w-3.5 h-3.5" /> Competitor analysis
              </div>
              <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-5 leading-tight">
                Reverse-engineer any channel's <span className="bg-gradient-to-br from-amber-300 to-red-500 bg-clip-text text-transparent">opening hook.</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Type a channel name → in 30 seconds, VidROI pulls their top 5
                videos, transcribes the first 300 words of each one with
                OpenAI Whisper, and runs a Claude-powered breakdown of the
                copywriting framework so you can study what&rsquo;s working in
                your niche.
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  "Top 5 videos by views — refreshed weekly",
                  "150-word hook transcripts (the exact opener that hooks 78% of viewers)",
                  "Likes, comments, click-through to watch — every metric in one row",
                  "Shareable 'hook teardown' pages — great for newsletter subscribers",
                ].map((b) => (
                  <li key={b} className="flex gap-2.5 text-zinc-300 text-sm">
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Demo video CTA strip ===== */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-semibold text-white mb-4">Watch the 2-minute demo.</h2>
          <p className="text-zinc-400 mb-7">See exactly how VidROI links a sale back to the video that drove it. No signup required.</p>
          <button
            type="button"
            onClick={() => setVideoOpen(true)}
            className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 transition-all group"
            data-testid="demo-cta"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play className="w-4 h-4 text-white ml-0.5" />
            </div>
            <span className="text-white font-semibold">Play demo video</span>
          </button>
        </div>
      </section>

      {/* ===== Pricing — two tiers (Feb 2026 restructure) ===== */}
      <section id="pricing" className="py-24 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold mb-3">Pricing</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-3">Two plans. Zero risk.</h2>
            <p className="text-zinc-400">14-day free trial on either plan. Cancel anytime — no charge until day 15.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-testid="pricing-card">
            {/* Starter */}
            <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-1">
              <div className="rounded-[22px] bg-zinc-950/70 p-8">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                  <div>
                    <div className="text-zinc-400 text-sm font-semibold uppercase tracking-widest mb-1">Starter</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-white">$29</span>
                      <span className="text-zinc-500">/ month</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">Full attribution + AI hook analysis, with fair-use caps for sustainable pricing.</p>
                  </div>
                </div>
                <ul className="space-y-2.5 mb-8 text-sm">
                  {[
                    "Unlimited tracked offers + connected channels",
                    "HMAC-signed tracking pixel + UTM links + short links",
                    "AI Hook Analysis — 250 competitor videos / mo",
                    "Blend Hooks with AI — 60 generations / mo",
                    "Ask AI (Claude Haiku) — 200 messages / mo",
                    "Weekly Monday-morning insight emails",
                    "Shareable hook teardown pages",
                  ].map((feat) => (
                    <li key={feat} className="flex gap-2.5 text-zinc-300">
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={goToLogin}
                  className="w-full bg-white/[0.06] hover:bg-white/[0.10] border border-white/10 text-white font-medium py-3 rounded-xl transition-colors"
                  data-testid="pricing-cta-starter"
                >
                  Start free 14-day trial · Starter
                </button>
              </div>
            </div>

            {/* Pro — highlighted */}
            <div className="rounded-3xl border border-amber-500/30 bg-gradient-to-b from-amber-500/[0.08] to-transparent p-1 shadow-[0_30px_80px_-20px_rgba(255,80,0,0.35)] relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-red-500 text-white text-[10px] font-bold uppercase tracking-widest">
                Most popular
              </div>
              <div className="rounded-[22px] bg-zinc-950 p-8">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                  <div>
                    <div className="text-amber-300 text-sm font-semibold uppercase tracking-widest mb-1">Pro</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-white">$79</span>
                      <span className="text-zinc-500">/ month</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">Unlimited AI analysis for creators shipping weekly.</p>
                  </div>
                  <span className="text-xs uppercase tracking-widest font-mono px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                    14-day free trial
                  </span>
                </div>
                <ul className="space-y-2.5 mb-8 text-sm">
                  {[
                    "Everything in Starter, plus:",
                    "Unlimited AI Hook Analysis",
                    "Unlimited Blend Hooks generations",
                    "Ask AI upgraded to Claude Sonnet 4.5 (deeper reasoning)",
                    "1,000 Ask AI messages / mo — 5× the Starter cap",
                    "Priority support + early access to new AI features",
                  ].map((feat, i) => (
                    <li key={feat} className={`flex gap-2.5 ${i === 0 ? "text-zinc-400 italic" : "text-zinc-200"}`}>
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${i === 0 ? "text-zinc-600" : "text-emerald-400"}`} />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={goToLogin}
                  className="w-full bg-gradient-to-br from-amber-500 to-red-500 hover:from-amber-400 hover:to-red-400 text-white font-semibold py-3 rounded-xl shadow-[0_8px_32px_-8px_rgba(255,80,0,0.6)] hover:shadow-[0_12px_40px_-8px_rgba(255,80,0,0.85)] transition-all"
                  data-testid="pricing-cta-pro"
                >
                  Start free 14-day trial · Pro
                </button>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-zinc-500 mt-6 flex items-center justify-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            Card collected up-front · cancel inside the trial, you&rsquo;re never charged
          </p>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-white text-center mb-10">Common questions</h2>
          <div>
            <FaqRow
              q="How does VidROI know which video drove a sale?"
              a="On connect, VidROI injects a unique HMAC-signed UTM link into each video's description. When a viewer clicks → lands on your offer → buys, the JS pixel on your checkout page tags the sale with the source video ID. The whole loop is invisible to the viewer and you don't have to manage any links manually."
            />
            <FaqRow
              q="Does this work with my YouTube account?"
              a="Yes, as long as you're the channel owner. We use Google's official OAuth flow to authenticate, then read your videos via the YouTube Data API v3. Read-only access by default — write access only kicks in if you opt-in to description auto-injection."
            />
            <FaqRow
              q="What payment processors do you support?"
              a="Stripe (full integration with deep attribution), Shopify (via webhook), and anything that can hit a CSV upload or our generic webhook endpoint. If you sell digital products via Gumroad, Lemonsqueezy, or your own custom checkout — we can attribute it."
            />
            <FaqRow
              q="Will my viewers see the tracking?"
              a="No. The UTM link looks like a normal URL. There's no banner, no pixel disclosure modal, no extra redirect. From the viewer's side it's identical to any other affiliate link a creator might use today."
            />
            <FaqRow
              q="What if I cancel during the trial?"
              a="Zero charge. We collect the card up-front to filter out tire-kickers, but Stripe doesn't actually run a transaction until day 15. Cancel any time inside that window from the billing portal — no email, no friction, no clawback."
            />
            <FaqRow
              q="Can I use VidROI for client channels (agency use)?"
              a="Yes. Each YouTube channel you OAuth-connect counts as one. Add as many as you manage — they all roll up into the same dashboard with per-channel filtering."
            />
          </div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="py-24 border-t border-white/5 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-gradient-to-r from-amber-500/10 via-red-500/10 to-amber-500/10 blur-3xl pointer-events-none"></div>
        <div className="relative max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-5xl font-semibold text-white mb-5 leading-tight">
            Find out which video pays your bills.
          </h2>
          <p className="text-lg text-zinc-400 mb-8">14 days free. Cancel anytime.</p>
          <button
            type="button"
            onClick={goToLogin}
            className="bg-gradient-to-br from-amber-500 to-red-500 hover:from-amber-400 hover:to-red-400 text-white font-semibold text-lg px-8 py-4 rounded-xl shadow-[0_8px_32px_-8px_rgba(255,80,0,0.6)] hover:shadow-[0_16px_48px_-8px_rgba(255,80,0,0.9)] transition-all"
            data-testid="final-cta"
          >
            Start free 14-day trial
          </button>
        </div>
      </section>

      <SiteFooter variant="marketing" />

      {videoOpen && <VideoModal onClose={() => setVideoOpen(false)} />}
    </div>
  );
}
